import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Mail, Eye, EyeOff, User, Phone } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', country_code: '+973', password: '' });
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const payload = isRegister ? form : { email: form.email, password: form.password };
      const response = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });
      login(response.data, response.data.token);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#040F1E] flex items-center justify-center px-4">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800;900&family=DM+Sans:wght@400;500;600;700;800&display=swap');
        .font-syne { font-family: 'Syne', sans-serif; }
        .font-dm { font-family: 'DM Sans', sans-serif; }
      `}</style>

      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/">
            <h1 className="font-syne text-5xl font-extrabold text-[#00C4B4] mb-2" style={{ letterSpacing: '-1.5px' }}>TAGO</h1>
          </Link>
          <p className="text-white/70 font-dm text-lg">{isRegister ? 'Create your account' : 'Welcome back'}</p>
        </div>

        <div className="bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-2xl p-8">
          {/* Google Login Button */}
          <button
            data-testid="google-login-button"
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-800 font-dm font-semibold py-3 px-6 rounded-xl transition-all mb-6"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-white/10"></div>
            <span className="text-white/40 text-sm font-dm">or</span>
            <div className="flex-1 h-px bg-white/10"></div>
          </div>

          <form onSubmit={handleSubmit}>
            {isRegister && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-dm font-semibold text-white/70 mb-2">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <input
                      data-testid="register-name-input"
                      type="text" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name"
                      className="w-full bg-[#040F1E] border border-[rgba(0,196,180,.2)] rounded-xl pl-10 pr-4 py-3 text-white font-dm focus:outline-none focus:border-[#00C4B4] transition-colors"
                      required
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-dm font-semibold text-white/70 mb-2">Phone Number</label>
                  <div className="flex gap-2">
                    <select
                      data-testid="register-country-code"
                      value={form.country_code}
                      onChange={(e) => setForm({ ...form, country_code: e.target.value })}
                      className="w-28 bg-[#040F1E] border border-[rgba(0,196,180,.2)] rounded-xl px-2 py-3 text-sm text-white focus:outline-none focus:border-[#00C4B4]"
                    >
                      <option value="+973">+973</option>
                      <option value="+971">+971</option>
                      <option value="+966">+966</option>
                      <option value="+974">+974</option>
                      <option value="+91">+91</option>
                    </select>
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                      <input
                        data-testid="register-phone-input"
                        type="tel" value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="Phone number"
                        className="w-full bg-[#040F1E] border border-[rgba(0,196,180,.2)] rounded-xl pl-10 pr-4 py-3 text-white font-dm focus:outline-none focus:border-[#00C4B4] transition-colors"
                        required
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="mb-4">
              <label className="block text-sm font-dm font-semibold text-white/70 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  data-testid="login-email-input"
                  type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="your@email.com"
                  className="w-full bg-[#040F1E] border border-[rgba(0,196,180,.2)] rounded-xl pl-10 pr-4 py-3 text-white font-dm focus:outline-none focus:border-[#00C4B4] transition-colors"
                  required
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-dm font-semibold text-white/70 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Password"
                  className="w-full bg-[#040F1E] border border-[rgba(0,196,180,.2)] rounded-xl pl-10 pr-11 py-3 text-white font-dm focus:outline-none focus:border-[#00C4B4] transition-colors"
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && <p data-testid="auth-error" className="text-red-400 text-sm mb-4 font-dm">{error}</p>}

            <button
              data-testid="auth-submit-button"
              type="submit" disabled={loading}
              className="w-full bg-[#00C4B4] hover:bg-[#00DDD0] text-[#040F1E] font-dm font-bold py-3 px-6 rounded-xl transition-all hover:shadow-lg hover:shadow-[#00C4B4]/30 disabled:opacity-50"
            >
              {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-white/50 text-sm mt-6 font-dm">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setIsRegister(!isRegister); setError(''); }}
              className="text-[#00C4B4] hover:underline ml-1 font-semibold"
            >
              {isRegister ? 'Sign In' : 'Create Account'}
            </button>
          </p>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-white/40 text-sm hover:text-white/60 font-dm transition-colors">Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
